import React, { useState, useEffect, useCallback } from 'react';
import {
  History, Filter, ChevronRight, Trash2, TrendingUp, TrendingDown,
  Shield, AlertTriangle, ArrowRight, Minus,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { historyService, reportsService } from '../services';
import { formatDate, truncateText } from '../lib/utils';
import { AppLayout } from '../components/AppLayout';
import { toast } from 'sonner';

import { EMPTY_STATES, DIMENSIONS, DIMENSION_ORDER } from '../design-system/content';
import { getScoreBandColor, getScoreBandClass, getDimensionColor } from '../design-system/tokens';
import { toHistoryItemVM, toReportVM } from '../design-system/viewmodels';
import { ScoreDisplay, PageHeader, EmptyState, StatusBadge, VerdictBadge } from '../components/shared';

const SCORE_RANGES = [
  { value: 'all', label: 'All Scores' },
  { value: '0-30', label: 'Poor (0-30)' },
  { value: '31-50', label: 'Acceptable (31-50)' },
  { value: '51-70', label: 'Good (51-70)' },
  { value: '71-85', label: 'Very Good (71-85)' },
  { value: '86-100', label: 'Excellent (86-100)' },
];

const DOMAINS = [
  { value: 'all', label: 'All Domains' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'mental_health', label: 'Mental Health' },
  { value: 'medical', label: 'Medical' },
  { value: 'coaching', label: 'Coaching' },
  { value: 'education', label: 'Education' },
  { value: 'financial', label: 'Financial' },
];

// ── Delta badge ──
function DeltaBadge({ delta }) {
  if (delta === null || delta === undefined) return null;
  const isPositive = delta > 0;
  const isZero = delta === 0;
  return (
    <span
      data-testid="delta-badge"
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
        isPositive ? 'bg-emerald-500/15 text-emerald-400' :
        isZero ? 'bg-white/5 text-gray-500' :
        'bg-red-500/15 text-red-400'
      }`}
    >
      {isPositive ? <TrendingUp className="w-2.5 h-2.5" /> :
       isZero ? <Minus className="w-2.5 h-2.5" /> :
       <TrendingDown className="w-2.5 h-2.5" />}
      {isPositive ? '+' : ''}{delta.toFixed(1)}
    </span>
  );
}

// ── Guard badge ──
function GuardBadge({ item }) {
  if (!item.hasOptimization) return null;
  if (item.returnedOriginal) {
    return (
      <span data-testid="guard-badge" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400">
        <Shield className="w-2.5 h-2.5" />
        {item.deltaGuard ? 'Delta Guard' : item.noHarmActivated ? 'No-Harm' : 'Guard'}
      </span>
    );
  }
  if (item.guardedMode) {
    const modeColors = {
      minimal: 'bg-sky-500/10 text-sky-400',
      moderate: 'bg-violet-500/10 text-violet-400',
      aggressive: 'bg-orange-500/10 text-orange-400',
    };
    return (
      <span data-testid="mode-badge" className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${modeColors[item.guardedMode] || 'bg-white/5 text-gray-400'}`}>
        {item.guardedMode}
      </span>
    );
  }
  return null;
}

// ── Rich Run Detail Modal ──
function RunDetail({ item }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await reportsService.getRunReport(item.id);
        setReport(toReportVM(data));
      } catch (err) {
        console.error('Failed to load report:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [item.id]);

  if (loading) return <div className="flex justify-center py-12"><span className="msml-spinner" /></div>;

  const hq = report?.hqScore ?? item.hqScore;
  const dims = report?.dimensions || DIMENSION_ORDER.map(k => ({
    key: k, label: DIMENSIONS[k]?.label || k, score: item.dimensionScores?.[k] ?? 0,
  }));

  return (
    <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1" data-testid="run-detail">

      {/* HQ Score + Delta Summary */}
      <div className="flex items-center gap-4 msml-panel p-4" data-testid="run-detail-score">
        <ScoreDisplay score={hq} size="md" showBand={false} />
        <div className="flex-1">
          <p className="text-sm text-[var(--msml-text-secondary)]">HQ Score</p>
          <p className="text-xs text-[var(--msml-text-muted)]">Band: {report?.scoreBand || item.scoreBand}</p>
          {report?.capsApplied?.length > 0 && (
            <StatusBadge label="Cap applied" variant="warning" className="mt-1" />
          )}
        </div>
        {item.hasOptimization && item.deltaOverall !== null && (
          <div className="text-right">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--msml-text-muted)]">{item.hqScore.toFixed(1)}</span>
              <ArrowRight className="w-3 h-3 text-[var(--msml-text-muted)]" />
              <span className={`text-sm font-semibold ${item.deltaOverall > 0 ? 'text-emerald-400' : item.deltaOverall < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                {(item.estimatedAfter ?? item.hqScore).toFixed(1)}
              </span>
            </div>
            <DeltaBadge delta={item.deltaOverall} />
          </div>
        )}
      </div>

      {/* Dimension Scores with deltas */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="run-detail-dimensions">
        {dims.map(d => {
          const dd = item.deltaDimensions?.[d.key];
          return (
            <div key={d.key} className="msml-panel p-3">
              <p className="text-[10px] text-[var(--msml-text-muted)] uppercase tracking-wider">{d.label}</p>
              <div className="flex items-baseline gap-2">
                <p className={`text-lg font-bold ${getScoreBandClass(d.score)}`}>{d.score?.toFixed(1)}</p>
                {dd?.delta != null && dd.delta !== 0 && (
                  <span className={`text-[10px] font-medium ${dd.delta > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {dd.delta > 0 ? '+' : ''}{dd.delta.toFixed(1)}
                  </span>
                )}
              </div>
              {dd && (
                <div className="mt-1.5 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, d.score)}%`,
                      backgroundColor: getDimensionColor(d.key),
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Guarded Optimization Summary */}
      {item.hasOptimization && item.guardedMode && (
        <div className="msml-panel p-4 space-y-2" data-testid="run-detail-guards">
          <p className="text-xs font-medium text-[var(--msml-text-secondary)]">Guarded Optimization</p>
          <div className="flex flex-wrap gap-2">
            <GuardBadge item={item} />
            {item.intentDrift && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400">
                <AlertTriangle className="w-2.5 h-2.5" /> Intent Drift
              </span>
            )}
            {item.guardsApplied.map((g, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-white/5 text-gray-500">
                {g.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
          {item.returnedOriginal && (
            <p className="text-[11px] text-amber-400/80 mt-1">
              {item.rejectedReason || 'Original text preserved by safety guard.'}
            </p>
          )}
          {item.fallbackReason && !item.returnedOriginal && (
            <p className="text-[11px] text-[var(--msml-text-muted)] mt-1">{item.fallbackReason}</p>
          )}
        </div>
      )}

      {/* Original text */}
      <div className="msml-panel p-3" data-testid="run-detail-original">
        <p className="text-xs text-[var(--msml-text-muted)] mb-1">Original Text</p>
        <p className="text-sm text-[var(--msml-text-secondary)] whitespace-pre-wrap">{report?.input?.original_text || item.originalText}</p>
      </div>

      {/* Optimized text */}
      {item.optimizedText && !item.returnedOriginal && (
        <div className="msml-panel p-3 border-l-2 border-[var(--msml-success)]" data-testid="run-detail-optimized">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-[var(--msml-success)]">Optimized Text</p>
            {item.guardedMode && (
              <span className="text-[10px] text-[var(--msml-text-muted)]">Mode: {item.guardedMode}</span>
            )}
          </div>
          <p className="text-sm text-[var(--msml-text-secondary)] whitespace-pre-wrap">{item.optimizedText}</p>
        </div>
      )}

      {/* Drivers */}
      {item.drivers?.length > 0 && (
        <div className="msml-panel p-3" data-testid="run-detail-drivers">
          <p className="text-xs text-[var(--msml-text-muted)] mb-2">Top Drivers</p>
          <div className="space-y-1.5">
            {item.drivers.slice(0, 6).map((d, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0`} style={{ backgroundColor: getDimensionColor(d.dimension) }} />
                <div>
                  <span className="text-[10px] text-[var(--msml-text-muted)] uppercase">{DIMENSIONS[d.dimension]?.short || d.dimension}</span>
                  <p className="text-xs text-[var(--msml-text-secondary)]">{d.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Judge */}
      {(item.judgeVerdict || report?.judge) && (
        <div className="msml-panel p-3" data-testid="run-detail-judge">
          <p className="text-xs text-[var(--msml-text-muted)] mb-1">Judge Verdict</p>
          <div className="flex items-center gap-3">
            <VerdictBadge verdict={(item.judgeVerdict || report?.judge)?.overall_verdict} />
            <span className="text-xs text-[var(--msml-text-muted)]">
              Intent: {(item.judgeVerdict || report?.judge)?.intent_status} | Safety: {(item.judgeVerdict || report?.judge)?.safety_status}
            </span>
          </div>
          {(item.judgeVerdict || report?.judge)?.notes && (
            <p className="text-xs text-[var(--msml-text-muted)] mt-1">{(item.judgeVerdict || report?.judge).notes}</p>
          )}
        </div>
      )}

      {/* Trace */}
      {report?.trace && (
        <div className="msml-panel p-3" data-testid="run-detail-trace">
          <p className="text-xs text-[var(--msml-text-muted)] mb-2">Trace</p>
          <div className="grid grid-cols-2 gap-1 text-[10px] text-[var(--msml-text-muted)]">
            <span>Rubric: {report.trace?.rubric_version}</span>
            <span>Prompt: {report.trace?.prompt_version}</span>
            <span>Scorer: {report.trace?.scoring_model_id}</span>
            <span>Status: {report.trace?.status}</span>
            <span className="col-span-2 font-mono break-all">Hash: {report.trace?.determinism_hash?.substring(0, 16)}...</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function HistoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [selectedRun, setSelectedRun] = useState(null);

  const [scoreRange, setScoreRange] = useState('all');
  const [domain, setDomain] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (scoreRange !== 'all') {
        const [min, max] = scoreRange.split('-').map(Number);
        filters.scoreMin = min;
        filters.scoreMax = max;
      }
      if (domain !== 'all') filters.domain = domain;

      const data = await historyService.getAll(20, offset, filters);
      setItems((data.items || []).map(toHistoryItemVM));
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoading(false);
    }
  }, [offset, scoreRange, domain]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleDelete = async (id) => {
    try {
      await historyService.delete(id);
      toast.success('Analysis deleted');
      loadHistory();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <AppLayout activeNav="history">
      <div className="p-4 lg:p-6">
        <div className="max-w-4xl mx-auto">
          <PageHeader
            icon={<History className="w-5 h-5 text-[var(--msml-primary)]" />}
            title="Analysis History"
            subtitle={`${total} persisted analyses`}
            actions={
              <Button
                variant="outline" size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="msml-btn-secondary text-xs"
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-3 h-3 mr-1" /> Filters
              </Button>
            }
          />

          {/* Filters */}
          {showFilters && (
            <div className="msml-card mb-4 flex flex-wrap gap-3 items-center" data-testid="history-filters">
              <Select value={scoreRange} onValueChange={(v) => { setScoreRange(v); setOffset(0); }}>
                <SelectTrigger className="w-40 msml-input" data-testid="filter-score-range">
                  <SelectValue placeholder="Score Range" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--msml-panel)] border border-[var(--msml-border)]">
                  {SCORE_RANGES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={domain} onValueChange={(v) => { setDomain(v); setOffset(0); }}>
                <SelectTrigger className="w-40 msml-input" data-testid="filter-domain">
                  <SelectValue placeholder="Domain" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--msml-panel)] border border-[var(--msml-border)]">
                  {DOMAINS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>

              <Button variant="ghost" size="sm" onClick={() => { setScoreRange('all'); setDomain('all'); setOffset(0); }}
                className="text-xs text-[var(--msml-text-muted)]"
              >
                Clear
              </Button>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-16"><span className="msml-spinner" /></div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<History className="w-12 h-12" />}
              title={EMPTY_STATES.history.title}
              body={EMPTY_STATES.history.body}
            />
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="liquid-card liquid-lift p-4 cursor-pointer group"
                  onClick={() => setSelectedRun(item)}
                  data-testid={`history-item-${item.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
                        <ScoreDisplay score={item.hqScore} size="sm" />
                        {item.hasOptimization && item.deltaOverall !== null && (
                          <>
                            <ArrowRight className="w-3.5 h-3.5 text-ink-muted" />
                            <span className={`text-base font-semibold ${
                              item.deltaOverall > 0 ? 'text-[var(--status-success-text)]' :
                              item.deltaOverall < 0 ? 'text-[var(--status-danger-text)]' : 'text-ink-3'
                            }`}>
                              {(item.estimatedAfter ?? item.hqScore).toFixed(1)}
                            </span>
                            <DeltaBadge delta={item.deltaOverall} />
                          </>
                        )}
                        <GuardBadge item={item} />
                      </div>
                      <p className="text-sm text-ink-2 line-clamp-2 leading-relaxed">{truncateText(item.originalText, 140)}</p>
                      <div className="flex items-center gap-2 mt-2.5 text-[10px] text-ink-muted flex-wrap">
                        <span className="liquid-pill !py-0.5 !px-2 !text-[10px]">{formatDate(item.createdAt)}</span>
                        {item.storageMode && <span className="liquid-pill !py-0.5 !px-2 !text-[10px] capitalize">{item.storageMode}</span>}
                        {item.rubricVersion && <span className="liquid-pill !py-0.5 !px-2 !text-[10px]">v{item.rubricVersion}</span>}
                        {item.hasOptimization && !item.returnedOriginal && (
                          <span className="liquid-pill liquid-pill--success !py-0.5 !px-2 !text-[10px]">Optimized</span>
                        )}
                        {item.returnedOriginal && (
                          <span className="liquid-pill liquid-pill--warning !py-0.5 !px-2 !text-[10px]">Original preserved</span>
                        )}
                        {item.hasJudge && (
                          <span className="liquid-pill liquid-pill--info !py-0.5 !px-2 !text-[10px]">Judged</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                        className="p-1.5 rounded-md hover:bg-white/5 text-ink-muted hover:text-[var(--status-danger-text)] opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-${item.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <ChevronRight className="w-4 h-4 text-ink-muted group-hover:text-ink-accent transition-colors" />
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {total > 20 && (
                <div className="flex items-center justify-center gap-3 pt-4">
                  <Button variant="outline" size="sm" disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - 20))} className="msml-btn-secondary text-xs">
                    Previous
                  </Button>
                  <span className="text-xs text-[var(--msml-text-muted)]">{offset + 1}–{Math.min(offset + 20, total)} of {total}</span>
                  <Button variant="outline" size="sm" disabled={offset + 20 >= total}
                    onClick={() => setOffset(offset + 20)} className="msml-btn-secondary text-xs">
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Run detail modal */}
          <Dialog open={!!selectedRun} onOpenChange={() => setSelectedRun(null)}>
            <DialogContent className="bg-[var(--msml-panel)] border border-[var(--msml-border)] text-[var(--msml-text-primary)] max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-[var(--msml-text-primary)] flex items-center gap-3">
                  Run Detail
                  {selectedRun?.guardedMode && (
                    <span className="text-xs font-normal text-[var(--msml-text-muted)]">
                      ({selectedRun.guardedMode} mode)
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>
              {selectedRun && <RunDetail item={selectedRun} />}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </AppLayout>
  );
}
